import React, { useEffect, useState } from 'react';
import * as Yup from "yup";
import { Col, Row, Card, CardHeader } from "reactstrap";
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import { Formik } from 'formik';
import ErrorComponent from 'Components/ErrorComponent';
import apiGlobal from 'global/api.global';
import { checkValidArray, handleServerResponse, isFormUnchanged, safeParse, useSafeFormikContext } from 'GenericForms/Helper';
import env from 'environment_system/env_system';
import { commonValidationMessages } from 'Components/ValidationErrorMessages';
import { useDocumentTitle } from 'Components/useDocument.hooks';
import LubeOilROBStructure from './LubeOilROBStructure';
import { RootState } from 'index';
import { useSelector, useDispatch } from 'react-redux';
import { getCurrentReportAction } from 'Store/Generic/ReportingSlice';
import FormLoading from 'Components/FormLoading';
import { useEditReport } from 'VesselReportingInfo/EditCompletedReports/EditReportProvider';

interface LubeOilROBType {
    setErrorMessage: any,
    toggleDynamicTabs?: any,
}

const LubeOilROBComponent = ({
    setErrorMessage,
    toggleDynamicTabs,
}: LubeOilROBType) => {
    const { editReportBool, previousEnggTabIndex } = useEditReport();
    /** State variables */
    useDocumentTitle('Lube Oil ROB Report - ecoSAIL');
    const { VesselID, ReportID, VoyageID, CurrentReport, PreviousReport, reportingLoading, ConfigurationLimits, reportingError } = useSelector((state: RootState) => state.Reporting);
    const dispatch = useDispatch();
    const lubeOilConsumptionCurrentReport = CurrentReport?.data?.chief_engineer?.lube_oil_consumption ?? null;
    const lubeOilROBCurrentReport = CurrentReport?.data?.chief_engineer?.reporting_lube_oil_rob ?? null;
    const lubeOilROBPreviousReport = PreviousReport?.data?.chief_engineer?.reporting_lube_oil_rob ?? null;
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [adjustmentpopup, setAdjustmentPopup] = useState(false);
    const [index, setIndex] = useState<number>(0);
    const formikContext = useSafeFormikContext<any>();
    const editValues = formikContext?.values;
    const setEditValues = formikContext?.setValues;
    const editInitialValues = formikContext?.initialValues;
    const editSubmit = formikContext?.submitForm;
    const editIsSubmitting = formikContext?.isSubmitting;
    const VesselLubeOils = ConfigurationLimits?.vessel_lube_oil_list || [];
    /** State variables end */

    /** Assign initial values to formik object */
    const getintialValueLubeOilROB = () => {
        let obj: any[] = [];
        if (checkValidArray(editValues?.chief_engineer?.lube_oil_consumption)) {
            obj = editValues.chief_engineer.lube_oil_consumption.map((item: any) => {
                const matchedLube = editValues?.chief_engineer?.reporting_lube_oil_rob?.find((lube: any) => lube.vessel_lube_oil === item.vessel_lube_oil);
                const previousROB = lubeOilROBPreviousReport?.find((lube: any) => lube.vessel_lube_oil === item.vessel_lube_oil);
                const EditedBunkering = editValues?.chief_engineer?.lube_oil_bunkering?.find(
                    (Lube: any) => Lube.vessel_lube_oil === item.vessel_lube_oil
                )?.bunkered;
                const EditedDebunkered = editValues?.chief_engineer?.lube_oil_debunkering?.find(
                    (Lube: any) => Lube.vessel_lube_oil === item.vessel_lube_oil
                )?.debunkered
                return {
                    ...item,
                    lube_name: matchedLube?.vessel_lube_oil_name ?? '',
                    debunkered: EditedDebunkered ?? 0,
                    bunkered: EditedBunkering ?? 0,
                    rob: parseFloat(previousROB?.rob || 0) + parseFloat(EditedBunkering || 0) - parseFloat(item?.consumed || 0) + parseFloat(item?.adjustment_quantity || 0) - parseFloat(EditedDebunkered || 0),
                    previous_rob: previousROB?.rob ?? 0,
                };
            });
            return obj;
        }
        if (checkValidArray(lubeOilConsumptionCurrentReport)) {
            return lubeOilConsumptionCurrentReport.map((item: any) => {
                const matchedLube = lubeOilROBCurrentReport?.find((lube: any) => lube.vessel_lube_oil === item.vessel_lube_oil);
                const previousROB = lubeOilROBPreviousReport?.find((lube: any) => lube.vessel_lube_oil === item.vessel_lube_oil);
                return {
                    ...item,
                    lube_name: matchedLube?.vessel_lube_oil_name ?? '',
                    debunkered: matchedLube?.debunkered ?? 0,
                    bunkered: matchedLube.bunkered ?? 0,
                    rob: matchedLube?.rob ?? 0,
                    previous_rob: previousROB?.rob ?? 0,
                };
            });
        } else if ((VesselLubeOils && obj.length <= VesselLubeOils.length)) {
            VesselLubeOils?.filter((item: any) => item.display === true)?.map((lube: any) => {
                const matchedLube = lubeOilROBCurrentReport?.find((rob: any) => rob.vessel_lube_oil === lube.id);
                const previousROB = lubeOilROBPreviousReport?.find((rob: any) => rob.vessel_lube_oil_name === lube.vessel_lube_oil_name);
                obj.push({
                    rob: parseFloat(previousROB?.rob || 0) + parseFloat(matchedLube?.bunkered || 0) - parseFloat(matchedLube?.debunkered || 0),
                    previous_rob: previousROB?.rob ?? 0,
                    consumed: 0,
                    vessel_lube_oil: lube.id,
                    lube_oil_name: lube.id,
                    lube_name: lube.vessel_lube_oil_name,
                    bunkered: matchedLube?.bunkered ?? 0,
                    debunkered: matchedLube?.debunkered ?? 0,
                    vessel: VesselID,
                    voyage_information: VoyageID,
                    vessel_reporting_information: ReportID,
                    adjustment_quantity: 0,
                    adjustment_reason: null as string,
                })
                return lube;
            })
            return obj;
        } else {
            return [
                {
                    rob: 0,
                    previous_rob: 0,
                    consumed: 0,
                    lube_oil_name: 0,
                    vessel: VesselID,
                    voyage_information: VoyageID,
                    vessel_reporting_information: ReportID,
                    adjustment_quantity: null as number,
                    adjustment_reason: null as string,
                }
            ]
        }
    }

    /** Lube oil Formik object */
    const LubeOilFormik = {
        initialValues: {
            lubeOil: getintialValueLubeOilROB()
        },
        validationSchema: Yup.object().shape({
            lubeOil: Yup.array().of(
                Yup.object().shape({
                    consumed: Yup.string()
                        .test('is-positive', 'Enter a valid value', value => Number(value) >= 0)
                        .matches(/^(?!-)\d{1,7}(\.\d{0,4})?$/, commonValidationMessages.before7after4)
                        .required(commonValidationMessages.required),
                    rob: Yup.string()
                        .matches(/^(?!-)/, commonValidationMessages.positiveROB),
                    adjustment_quantity: Yup.string().matches(
                        /^[+-]?\d{0,7}(?:\.\d{1,4})?$/,
                        commonValidationMessages.before7after4
                    ),
                    adjustment_reason: Yup.string().when('adjustment_quantity', (val: any, schema: any) => {
                        if (parseFloat(val) !== 0) {
                            return schema.min(2, commonValidationMessages.min2)
                                .max(200, commonValidationMessages.max200)
                                .required(commonValidationMessages.required);
                        }
                        return schema.nullable();
                    }),
                })
            )
        })
    };

    /** useEffect start */
    useEffect(() => {
        let prevArr: any[] = [];
        if (checkValidArray(lubeOilConsumptionCurrentReport)) {
            lubeOilConsumptionCurrentReport.map((rob: any) => {
                return prevArr.push({
                    lubeOil: rob.lube_oil_name,
                    rob: rob.rob
                });
            })
        } else if (checkValidArray(lubeOilROBPreviousReport)) {
            lubeOilROBPreviousReport.map((rob: any) => {
                return prevArr.push({
                    lubeOil: rob.lube_oil_name,
                    rob: rob.rob
                });
            })
        }
    }, [lubeOilROBPreviousReport, lubeOilConsumptionCurrentReport])
    useEffect(() => {
        LubeOilFormik.initialValues.lubeOil = getintialValueLubeOilROB();
        setRefreshKey(refreshKey + 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editReportBool, lubeOilROBPreviousReport, lubeOilConsumptionCurrentReport, VesselLubeOils])
    /** useEffect end */

    const handleSave = (index: number, values: any, setFieldValue: (field: string, value: any,) => void) => {
        setAdjustmentPopup(false);
        const adjustmentQuantity = (safeParse(values?.lubeOil[index]?.adjustment_quantity) || 0);
        const loConsumption = (safeParse(values?.lubeOil[index]?.consumed) || 0);
        const quantityBunkered = (safeParse(values?.lubeOil[index]?.bunkered) || 0);
        const quantityDebunkered = (safeParse(values?.lubeOil[index]?.debunkered) || 0);
        const previousROB = (safeParse(values?.lubeOil[index]?.previous_rob) || 0);
        const newROB = previousROB + quantityBunkered + adjustmentQuantity - loConsumption - quantityDebunkered;
        setFieldValue(`lubeOil.${index}.rob`, newROB);
        setAdjustmentPopup(false);
    };

    return (
        <Card className='p-0 mb-0 border-0'>
            <CardHeader className='p-2 border-bottom-0'>
                <div className="text-center">
                    <Row>
                        <Col>
                            <h4 className="page_title pos-start mb-0">Lube Oil ROB</h4>
                            <p className="card-title-desc pos-start">All readings since last report</p>
                        </Col>
                        <Col>
                            <ReportDetailsHeader />
                        </Col>
                    </Row>
                </div>
            </CardHeader>
            {(reportingLoading) && <FormLoading />}
            {reportingError && <ErrorComponent message='Unable to load required data!' />}
            {!(reportingLoading) &&
                !reportingError &&
                <Formik
                    onSubmit={async (values: any, actions: any) => {
                        if (env?.form_validation === false || (isFormUnchanged(LubeOilFormik.initialValues, values) && values?.lubeOil[0]?.id > 0)) {
                            setErrorMessage(null)
                            toggleDynamicTabs(previousEnggTabIndex + 1);
                            return;
                        }
                        actions.setSubmitting(true);
                        const responseArray: any = [];
                        const handleResponse = () => {
                            dispatch(getCurrentReportAction(VesselID, ReportID));
                            if (env?.form_validation === true) {
                                setErrorMessage(null)
                                toggleDynamicTabs(previousEnggTabIndex + 1);
                            }
                            actions.setSubmitting(false);
                        }
                        /** Lube oil ROB submit */
                        if (checkValidArray(lubeOilConsumptionCurrentReport)) {
                            values.lubeOil.map((lube: any) => {
                                responseArray.push(apiGlobal.put(`/lube_oil_consumption/${lube.id}/`, lube));
                                return '';
                            })
                        } else {
                            responseArray.push(apiGlobal.post(`/lube_oil_consumption/`, values.lubeOil));
                        }
                        const response = await handleServerResponse(responseArray);
                        if (response === true) {
                            handleResponse();
                            actions.setSubmitting(false);
                        } else {
                            setErrorMessage(response)
                        }
                    }}
                    initialValues={LubeOilFormik.initialValues}
                    validationSchema={env?.form_validation === true ? LubeOilFormik.validationSchema : null}
                    key={refreshKey}
                >
                    <LubeOilROBStructure
                        VesselLubeOils={VesselLubeOils}
                        setEditValues={setEditValues}
                        editValues={editValues}
                        LubeOilFormik={LubeOilFormik}
                        refreshKey={refreshKey}
                        toggleDynamicTabs={toggleDynamicTabs}
                        setErrorMessage={setErrorMessage}
                        setRefreshKey={setRefreshKey}
                        handleSave={handleSave}
                        setAdjustmentPopup={setAdjustmentPopup}
                        adjustmentpopup={adjustmentpopup}
                        index={index}
                        setIndex={setIndex}
                        editInitialValues={editInitialValues}
                        editSubmit={editSubmit}
                        editIsSubmitting={editIsSubmitting}
                    />
                </Formik>
            }
        </Card>
    )
}

export default LubeOilROBComponent